import type { GroupSubscriptionData } from '@shared/dream-components';

import type { Subscription, SubscriptionPremiumTier } from '~/interfaces/subscriptions';
import { PriceInterval } from '~/interfaces/subscriptions';

/**
 * TEMPORARY DEV SCAFFOLDING — remove with BEE-18984.
 *
 * Hardcoded preset scenarios for the General-tab Figma states. Override only
 * swaps `premium_tiers` (+ revoked field), keeping subscriber identity real —
 * so modals still render correctly even if their actions fail against fake IDs.
 */

export type GeneralScenarioKey =
  | 'live'
  | 'groupOnly'
  | 'groupAndIndividual'
  | 'groupAndIndividualDifferentTier'
  | 'adminAndIndividual'
  | 'adminAndIndividualDifferentTier'
  | 'freeOnly';

export const GENERAL_SCENARIO_LABELS: Record<GeneralScenarioKey, string> = {
  live: 'Live data (real subscription)',
  groupOnly: 'Group sub only (managed by admin)',
  groupAndIndividual: 'Occupant + individual sub (same tier, overlap)',
  groupAndIndividualDifferentTier: 'Occupant + individual sub (different tier, no overlap)',
  adminAndIndividual: 'Group admin + individual sub (same tier, overlap)',
  adminAndIndividualDifferentTier: 'Group admin + individual sub (different tier, no overlap)',
  freeOnly: 'Back to free (paid plan ended)',
};

// Shares the 'Premium' tier name with individualPaidTier so the occupant
// overlap scenario (groupAndIndividual) is a genuine same-tier double-pay.
const groupManagedTier: SubscriptionPremiumTier = {
  id: 'tier_mock_group_all_access',
  name: 'Premium',
  permanent: false,
  gift_details: null,
  is_gift: false,
  tier_price_id: 'price_mock_group_all_access_year',
  is_downgrading: false,
  cancel_at: null,
  is_trialing: false,
  is_archived: false,
  paused: false,
  price_preview: {
    id: 'price_mock_group_all_access_year',
    amount_cents: 2000,
    original_amount_cents: 2000,
    currency: 'USD',
    interval: PriceInterval.YEAR,
    denominator: 'year',
    cta: 'Subscribe',
    features: ['Full archive access', 'Subscriber-only posts', 'Premium podcasts'],
  },
  group_managed_by: { admin_email: 'george@acme.com', group_name: 'Acme' },
};

// 'All Access' differs from the individual 'Premium' tier, so the occupant
// no-overlap scenario proves the banner stays hidden across different tiers.
const groupManagedDifferentTier: SubscriptionPremiumTier = {
  ...groupManagedTier,
  name: 'All Access',
};

const individualPaidTier: SubscriptionPremiumTier = {
  id: 'tier_mock_individual_premium',
  name: 'Premium',
  permanent: false,
  gift_details: null,
  is_gift: false,
  tier_price_id: 'price_mock_individual_premium_month',
  is_downgrading: false,
  cancel_at: null,
  is_trialing: false,
  is_archived: false,
  paused: false,
  price_preview: {
    id: 'price_mock_individual_premium_month',
    amount_cents: 1000,
    original_amount_cents: 1000,
    currency: 'USD',
    interval: PriceInterval.MONTH,
    denominator: 'month',
    cta: 'Subscribe',
    features: ['Subscriber-only posts', 'Comments'],
  },
};

const SCENARIO_TIERS: Record<GeneralScenarioKey, SubscriptionPremiumTier[] | null> = {
  // null = pass real subscription through untouched
  live: null,
  groupOnly: [groupManagedTier],
  groupAndIndividual: [groupManagedTier, individualPaidTier],
  groupAndIndividualDifferentTier: [groupManagedDifferentTier, individualPaidTier],
  // Admin's group access comes from the group sub (see getScenarioGroupSubscriptions),
  // not a group_managed_by tier — so their premium_tiers hold only the individual plan.
  adminAndIndividual: [individualPaidTier],
  adminAndIndividualDifferentTier: [individualPaidTier],
  freeOnly: [],
};

export function getScenarioSubscription(scenario: GeneralScenarioKey, baseSubscription: Subscription): Subscription {
  const tiers = SCENARIO_TIERS[scenario];
  if (tiers === null) return baseSubscription;
  const groupAccessRevokedFrom = scenario === 'freeOnly' ? { admin_email: 'george@acme.com' } : null;
  return { ...baseSubscription, premium_tiers: tiers, group_access_revoked_from: groupAccessRevokedFrom };
}

const adminGroupSubscription: GroupSubscriptionData = {
  id: 'group_mock_admin',
  purchaser_email: 'admin@acme.com',
  status: 'active',
  seats_filled: 1,
  max_seats: 5,
  invites_pending: 0,
  total_amount_cents: 10000,
  per_seat_amount_cents: 2000,
  currency: 'USD',
  interval: 'year',
  // Matches the individual 'Premium' tier so adminAndIndividual is a genuine
  // same-tier overlap (BEE-20105).
  tier_name: 'Premium',
  renews_at: null,
  price_id: 'price_mock_group_all_access_year',
  group_name: 'Acme',
  seats: [],
};

// Same active admin group on a different tier ('All Access') than the
// individual 'Premium' plan — the literal BEE-20105 bug case: distinct plans
// must not surface the double-pay banner.
const adminGroupSubscriptionDifferentTier: GroupSubscriptionData = {
  ...adminGroupSubscription,
  id: 'group_mock_admin_different_tier',
  tier_name: 'All Access',
};

/**
 * Only the admin scenarios inject a synthetic group — every other scenario
 * passes the live loader groups through, so the dev picker can't mask a real
 * group the subscriber actually manages.
 */
export function getScenarioGroupSubscriptions(
  scenario: GeneralScenarioKey,
  liveGroups: GroupSubscriptionData[],
): GroupSubscriptionData[] {
  if (scenario === 'adminAndIndividual') return [adminGroupSubscription];
  if (scenario === 'adminAndIndividualDifferentTier') return [adminGroupSubscriptionDifferentTier];
  return liveGroups;
}
